import React from 'react';
import { DeleteWarning, Modal, SaveButton, SystemText, DebugInfo } from 'e4shared';
import { useDispatch, useSelector } from 'react-redux';
import Sorter from 'components/admin/forms/sorter';
import NoResults from 'components/general/noResults';
import ManageTask from 'views/user/components/manageTask';
import TaskIcon from 'views/user/task/components/taskIcon';
import { toggleFormHelp } from 'actions/ui';
import { apiDelete, apiPatch } from 'services';

export const ObjectiveManager = ({ show, close, reload, pathway }) => {
    if (!pathway?.tasks) return null;

    const dispatch = useDispatch();

    const [showManageTask, setShowManageTask] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [task, setTask] = React.useState({});
    const [items, setItems] = React.useState([]);
    const [order, setOrder] = React.useState([]);
    const showFormHelp = useSelector(state => state.ui.showFormHelp);

    const user = useSelector(state => state.user.user);

    const st = (path, ...rest) => SystemText(`Admin/Pathways/${path}`, ...rest);

    const onSave = () => {
        const promises = [];

        order.forEach(x => {
            promises.push(apiPatch('task', x, user.guid));
        });

        Promise.all(promises).then(() => {
            setSaving(false);
            close();
        });
    };

    const api = {
        remove: id => apiDelete(`task/${id}`, null, user.guid, reload),
    };

    React.useEffect(() => {
        DebugInfo('component', 'ObjectiveManager');
        DebugInfo('pathway-id', pathway?.id?.toString() || '');
        DebugInfo('pathway-name', pathway?.name || '');
    }, [pathway]);

    React.useEffect(() => {
        const temp = [...pathway.tasks].map(t => {
            const item = {
                ...t,
                id: t.task_Id,
                icon: () => <TaskIcon icons={t.icons} />,
                extra: () => {
                    return (
                        <div className="table-btns">
                            <button
                                data-testid="objective-edit-button"
                                className="btn btn-xs btn-info"
                                onClick={() => {
                                    setTask({ ...t, id: t.task_Id });
                                    setShowManageTask(true);
                                }}
                            >
                                <i className="fa fa-pencil" />
                            </button>
                            <button
                                data-testid="objective-delete-button"
                                className="btn btn-xs btn-danger"
                                onClick={() => {
                                    setTask({ ...t, id: t.task_Id });
                                    setShowDelete(true);
                                }}
                            >
                                <i className="fa fa-trash" />
                            </button>
                        </div>
                    );
                },
            };

            return item;
        });

        setItems(temp);
    }, [pathway?.id, pathway?.tasks]);

    const footer = (
        <div>
            <SaveButton
                id="objectives-save-button"
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                onClick={onSave}
            >
                {st('Buttons/Save')}
            </SaveButton>
            <button data-testid="objectives-save-button" id="cancel-button" className="btn" onClick={close}>
                {st('Buttons/Cancel')}
            </button>
        </div>
    );

    return (
        <Modal
            title={st('Objectives/Title')}
            footer={pathway.tasks.length === 0 ? null : footer}
            show={show}
            close={close}
            toggleFormHelp={() => dispatch(toggleFormHelp())}
        >
            <div className="btn-row text-right">
                <button
                    data-testid="objectives-add-button"
                    className="btn btn-success"
                    onClick={() => {
                        setTask({
                            pathway_Id: pathway.id,
                            due: pathway.date_Due,
                            sequence: (pathway?.tasks?.length || 0) + 1,
                        });
                        setShowManageTask(true);
                    }}
                >
                    <i className="fa fa-calendar-plus mr10" />
                    {st('Buttons/NewObjective')}
                </button>
            </div>
            {pathway.tasks.length === 0 ? (
                <div className="mt10">
                    <NoResults title={st('Objectives/NoResults/Title')} message={st('Objectives/NoResults/Message')} />
                </div>
            ) : (
                <div>
                    <Sorter
                        label={st('Objectives/Sorter/Label')}
                        help={showFormHelp ? st('Objectives/Sorter/Help') : ''}
                        options={items}
                        narrow={false}
                        distance={5}
                        onChange={options => setOrder(options)}
                    />
                </div>
            )}
            <ManageTask
                key={`manage-task-${task?.id}`}
                values={{ ...task }}
                anonymous
                show={showManageTask}
                reload={reload}
                close={() => setShowManageTask(false)}
            />
            <DeleteWarning
                key={`delete-task-${task?.id}`}
                message={st('Objectives/Warning')}
                show={showDelete}
                close={confirm => {
                    setShowDelete(false);
                    confirm && api.remove(task.id);
                }}
            />
        </Modal>
    );
};

export default ObjectiveManager;
